import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Filter, Gauge, Pencil, Plus } from "lucide-react";

import { ApiError } from "../../lib/api";
import { useToast } from "../../components/Toast";
import { Modal } from "../../components/Modal";
import {
  Badge,
  Button,
  Card,
  Cargando,
  ErrorCarga,
  Input,
  PageHeader,
  Select,
  TableWrap,
  controlClasses,
  type BadgeTone,
  useDescarteProtegido,
  useHayCambios,
} from "../../components/ui";
import {
  actualizarMedidor,
  crearMedidor,
  listarArranques,
  listarMedidores,
  type ArranqueRead,
  type EstadoMedidor,
  type MedidorCreatePayload,
  type MedidorRead,
  type MedidorUpdatePayload,
} from "./api";

const ESTADOS: EstadoMedidor[] = [
  "Bueno",
  "Malo",
  "En Reparación",
  "De Baja",
];

type Filtro = "todos" | "bodega" | "instalados";

export function MedidoresListPage(): JSX.Element {
  const [filtro, setFiltro] = useState<Filtro>("todos");
  const [editing, setEditing] = useState<MedidorRead | "new" | null>(null);

  const medidoresQ = useQuery({
    queryKey: [
      "medidores",
      filtro === "todos"
        ? "todos"
        : filtro === "bodega"
          ? "bodega"
          : "instalados",
    ],
    queryFn: () =>
      listarMedidores(
        filtro === "todos" ? undefined : filtro === "bodega",
      ),
  });
  const arranquesQ = useQuery({
    queryKey: ["arranques", "todos"],
    queryFn: () => listarArranques(),
  });

  const arranqueById = useMemo(() => {
    const m = new Map<number, ArranqueRead>();
    for (const a of arranquesQ.data ?? []) m.set(a.id, a);
    return m;
  }, [arranquesQ.data]);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Medidores"
        icon={Gauge}
        subtitle={
          <>
            Inventario físico de medidores. Para instalar/retirar un medidor en
            un arranque, ve al módulo Arranques (expande la fila).
            {medidoresQ.data && <> ({medidoresQ.data.length} medidores)</>}
          </>
        }
        actions={
          <Button icon={Plus} onClick={() => setEditing("new")}>
            Nuevo medidor
          </Button>
        }
      />

      {editing && (
        <MedidorForm
          medidor={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}

      <Card padding="sm">
        <div className="flex items-center gap-2 text-sm">
          <Filter className="h-4 w-4 text-neutral-500" strokeWidth={1.5} />
          <select
            value={filtro}
            onChange={(e) => setFiltro(e.target.value as Filtro)}
            aria-label="Filtrar medidores"
            className={controlClasses}
          >
            <option value="todos">Todos</option>
            <option value="bodega">En bodega (sin instalar)</option>
            <option value="instalados">Instalados</option>
          </select>
        </div>
      </Card>

      {medidoresQ.isPending && <Cargando />}
      {medidoresQ.isError && (
        <ErrorCarga
          error={medidoresQ.error}
          mensaje="Error al cargar medidores."
          onReintentar={() => void medidoresQ.refetch()}
        />
      )}
      {medidoresQ.data && medidoresQ.data.length === 0 && (
        <p className="text-sm text-neutral-500">Sin medidores.</p>
      )}

      {medidoresQ.data && medidoresQ.data.length > 0 && (
        <TableWrap label="Medidores">
          <table className="min-w-full divide-y divide-neutral-200 text-sm">
            <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th scope="col" className="px-3 py-2 text-left">N° serie</th>
                <th scope="col" className="px-3 py-2 text-left">Marca</th>
                <th scope="col" className="px-3 py-2 text-left">Tipo / Diámetro</th>
                <th scope="col" className="px-3 py-2 text-left">Ubicación</th>
                <th scope="col" className="px-3 py-2 text-left">Estado</th>
                <th scope="col" className="px-3 py-2 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {medidoresQ.data.map((m) => (
                <tr key={m.id} className="hover:bg-neutral-50">
                  <td className="px-3 py-2 font-mono text-xs">
                    {m.numero_serie}
                  </td>
                  <td className="px-3 py-2 text-neutral-700">{m.marca ?? "—"}</td>
                  <td className="px-3 py-2 text-neutral-600 text-xs">
                    {m.tipo} · {m.diametro}
                    {m.subtipo && <div>{m.subtipo}</div>}
                  </td>
                  <td className="px-3 py-2">
                    {m.arranque_id != null ? (
                      <div className="text-neutral-700">
                        {arranqueById.get(m.arranque_id)?.codigo_arranque ??
                          `#${m.arranque_id}`}
                        <div className="text-xs text-neutral-500">
                          {arranqueById.get(m.arranque_id)?.direccion ?? ""}
                        </div>
                      </div>
                    ) : (
                      <span className="text-xs text-neutral-500 italic">
                        en bodega
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <EstadoBadge estado={m.estado} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button
                      variant="link"
                      size="sm"
                      icon={Pencil}
                      onClick={() => setEditing(m)}
                    >
                      Editar
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}
    </div>
  );
}

function MedidorForm({
  medidor,
  onClose,
}: {
  medidor: MedidorRead | null;
  onClose: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const editando = medidor !== null;

  const [numeroSerie, setNumeroSerie] = useState(medidor?.numero_serie ?? "");
  const [marca, setMarca] = useState(medidor?.marca ?? "");
  const [diametro, setDiametro] = useState(medidor?.diametro ?? "13mm");
  const [tipo, setTipo] = useState(medidor?.tipo ?? "Mecanico");
  const [subtipo, setSubtipo] = useState(medidor?.subtipo ?? "");
  const [tecnologia, setTecnologia] = useState(medidor?.tecnologia ?? "");
  const [estado, setEstado] = useState<EstadoMedidor>(
    medidor?.estado ?? "Bueno",
  );
  const [lecturaInicial, setLecturaInicial] = useState(
    String(medidor?.lectura_inicial ?? 0),
  );
  const [error, setError] = useState<string | null>(null);

  const crearMut = useMutation({
    mutationFn: (p: MedidorCreatePayload) => crearMedidor(p),
    onSuccess: (m) => {
      toast.success(`Medidor ${m.numero_serie} agregado a bodega`);
      void qc.invalidateQueries({ queryKey: ["medidores"] });
      onClose();
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "No se pudo crear";
      setError(msg);
      toast.error(msg);
    },
  });

  const actualizarMut = useMutation({
    mutationFn: (p: MedidorUpdatePayload) =>
      actualizarMedidor((medidor as MedidorRead).id, p),
    onSuccess: (m) => {
      toast.success(`Medidor ${m.numero_serie} actualizado`);
      void qc.invalidateQueries({ queryKey: ["medidores"] });
      onClose();
    },
    onError: (err) => {
      const msg =
        err instanceof ApiError ? err.message : "No se pudo actualizar";
      setError(msg);
      toast.error(msg);
    },
  });

  const isPending = crearMut.isPending || actualizarMut.isPending;

  function submit(): void {
    setError(null);
    if (!editando && !numeroSerie.trim())
      return setError("Número de serie requerido");

    const li = Number(lecturaInicial);
    if (!editando && (!Number.isFinite(li) || li < 0))
      return setError("Lectura inicial debe ser ≥ 0");

    if (editando) {
      actualizarMut.mutate({
        marca: marca.trim() || null,
        diametro,
        tipo,
        subtipo: subtipo.trim() || null,
        tecnologia: tecnologia.trim() || null,
        estado,
      });
    } else {
      crearMut.mutate({
        numero_serie: numeroSerie.trim(),
        marca: marca.trim() || null,
        diametro,
        tipo,
        subtipo: subtipo.trim() || null,
        tecnologia: tecnologia.trim() || null,
        estado,
        lectura_inicial: li,
      });
    }
  }

  // Cambios sin guardar: cerrar pregunta antes de descartar (auditoría UX-01).
  const hayCambios = useHayCambios([
    numeroSerie, marca, diametro, tipo, subtipo, tecnologia, estado, lecturaInicial,
  ]);
  const { cerrar: intentarCerrar, dialogo: dialogoDescarte } = useDescarteProtegido(
    hayCambios,
    onClose,
    crearMut.isPending || actualizarMut.isPending,
  );

  return (
    <Modal
      open
      onClose={intentarCerrar}
      title={editando ? "Editar medidor" : "Nuevo medidor"}
      subtitle={editando ? medidor.numero_serie : undefined}
      icon={Gauge}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={intentarCerrar} disabled={isPending}>
            Cancelar
          </Button>
          <Button onClick={submit} disabled={isPending}>
            {isPending
              ? editando
                ? "Guardando…"
                : "Creando…"
              : editando
                ? "Guardar cambios"
                : "Crear medidor"}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Input
          label={editando ? "N° serie (no editable)" : "N° serie"}
          required={!editando}
          type="text"
          maxLength={50}
          value={numeroSerie}
          onChange={(e) => setNumeroSerie(e.target.value)}
          disabled={isPending || editando}
          autoFocus={!editando}
        />
        <Input
          label="Marca"
          type="text"
          maxLength={50}
          value={marca}
          onChange={(e) => setMarca(e.target.value)}
          disabled={isPending}
          placeholder="ej. ELSTER"
        />
        <Select
          label="Diámetro"
          value={diametro}
          onChange={(e) => setDiametro(e.target.value)}
          disabled={isPending}
        >
          {["13mm", "15mm", "20mm", "25mm", "40mm"].map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </Select>

        <Select
          label="Tipo"
          value={tipo}
          onChange={(e) => setTipo(e.target.value)}
          disabled={isPending}
        >
          <option value="Mecanico">Mecánico</option>
          <option value="Electronico">Electrónico</option>
          <option value="Smart">Smart</option>
        </Select>
        <Input
          label="Subtipo"
          type="text"
          maxLength={50}
          value={subtipo}
          onChange={(e) => setSubtipo(e.target.value)}
          disabled={isPending}
        />
        <Input
          label="Tecnología"
          type="text"
          maxLength={50}
          value={tecnologia}
          onChange={(e) => setTecnologia(e.target.value)}
          disabled={isPending}
        />

        <Select
          label="Estado"
          value={estado}
          onChange={(e) => setEstado(e.target.value as EstadoMedidor)}
          disabled={isPending}
        >
          {ESTADOS.map((e) => (
            <option key={e} value={e}>
              {e}
            </option>
          ))}
        </Select>
        {!editando && (
          <Input
            label="Lectura inicial (m³)"
            type="number"
            min={0}
            value={lecturaInicial}
            onChange={(e) => setLecturaInicial(e.target.value)}
            disabled={isPending}
          />
        )}
      </div>

      {error && <p className="mt-3 text-sm text-rose-700">{error}</p>}
      {dialogoDescarte}
    </Modal>
  );
}

const ESTADO_TONE: Record<EstadoMedidor, BadgeTone> = {
  Bueno: "emerald",
  Malo: "rose",
  "En Reparación": "amber",
  "De Baja": "slate",
};

function EstadoBadge({ estado }: { estado: EstadoMedidor }): JSX.Element {
  return <Badge tone={ESTADO_TONE[estado]}>{estado}</Badge>;
}
